import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import styles from './Check.module.scss';

type DomainStatus = 'checking' | 'success' | 'error';

interface DomainCheck {
  domain: string;
  type: 'http' | 'websocket';
  status: DomainStatus;
}

interface DomainConfig {
  domain: string;
  type: 'http' | 'websocket';
}

const DOMAINS_TO_CHECK: DomainConfig[] = [
  { domain: 'ph.lseg.reflexivity.com', type: 'http' },
  { domain: 'auth.lseg.reflexivity.com', type: 'http' },
  { domain: 'api.lseg.reflexivity.com', type: 'http' },
  { domain: 'lseg.reflexivity.com', type: 'http' },
  { domain: 'cdn.reflexivity.com', type: 'http' },
  { domain: 'ws.lseg.reflexivity.com', type: 'websocket' },
  { domain: 'img.reflexivity.com', type: 'http' },
];

const checkHttpDomain = async (domain: string): Promise<boolean> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    await fetch(`https://${domain}/`, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return true;
  } catch {
    return false;
  }
};

const checkWebSocketDomain = (domain: string): Promise<boolean> => {
  return new Promise(resolve => {
    let resolved = false;

    const resolveOnce = (value: boolean) => {
      if (!resolved) {
        resolved = true;
        resolve(value);
      }
    };

    try {
      const ws = new WebSocket(`wss://${domain}`);

      const timeout = setTimeout(() => {
        ws.close();
        resolveOnce(false);
      }, 5000);

      ws.onopen = () => {
        clearTimeout(timeout);
        ws.close();
        resolveOnce(true);
      };

      // onclose fires when connection closes - if we got here, server was reachable
      ws.onclose = event => {
        clearTimeout(timeout);

        // Code 1006 with wasClean=false typically means network failure
        // Any other close means server was reached
        if (event.code === 1006 && !event.wasClean) {
          // Could be network failure, but also could be server rejecting
          // Try HTTP fallback to confirm reachability
          checkHttpDomain(domain).then(resolveOnce);
        } else {
          resolveOnce(true);
        }
      };

      ws.onerror = () => {
        // Error alone doesn't mean unreachable - wait for onclose
      };
    } catch {
      resolveOnce(false);
    }
  });
};

const checkDomain = async (config: DomainConfig): Promise<boolean> => {
  if (config.type === 'websocket') {
    return checkWebSocketDomain(config.domain);
  }

  return checkHttpDomain(config.domain);
};

const getStatusLabel = (status: DomainStatus): string => {
  switch (status) {
    case 'checking':
      return 'Checking...';
    case 'success':
      return 'Reachable';
    case 'error':
      return 'Unreachable';
  }
};

const statusIndicatorClass = (status: DomainStatus) => {
  switch (status) {
    case 'checking':
      return styles.statusIndicatorChecking;
    case 'success':
      return styles.statusIndicatorSuccess;
    case 'error':
      return styles.statusIndicatorError;
  }
};

const statusDotClass = (status: DomainStatus) => {
  switch (status) {
    case 'checking':
      return styles.statusDotChecking;
    case 'success':
      return styles.statusDotSuccess;
    case 'error':
      return styles.statusDotError;
  }
};

export const Check = () => {
  const [domainChecks, setDomainChecks] = useState<DomainCheck[]>(
    DOMAINS_TO_CHECK.map(({ domain, type }) => ({
      domain,
      type,
      status: 'checking',
    }))
  );
  const [isChecking, setIsChecking] = useState(false);

  const runChecks = useCallback(async () => {
    setIsChecking(true);
    setDomainChecks(
      DOMAINS_TO_CHECK.map(({ domain, type }) => ({
        domain,
        type,
        status: 'checking',
      }))
    );

    for (const config of DOMAINS_TO_CHECK) {
      const isReachable = await checkDomain(config);
      setDomainChecks(prev =>
        prev.map(check =>
          check.domain === config.domain
            ? { ...check, status: isReachable ? 'success' : 'error' }
            : check
        )
      );
    }

    setIsChecking(false);
  }, []);

  useEffect(() => {
    runChecks();
  }, [runChecks]);

  const allChecked = domainChecks.every(check => check.status !== 'checking');
  const allSuccess = domainChecks.every(check => check.status === 'success');
  const successCount = domainChecks.filter(
    check => check.status === 'success'
  ).length;

  return (
    <>
      <Helmet>
        <title>Connectivity Check</title>
      </Helmet>

      <main className={styles.container} data-testid="connectivity-check-page">
        <div className={styles.card}>
          <h1 className={styles.title}>Connectivity Check</h1>
          <p className={styles.subtitle}>
            Verifying access to required services
          </p>

          <ul className={styles.domainList}>
            {domainChecks.map(({ domain, type, status }) => (
              <li key={domain} className={styles.domainItem}>
                <div className={styles.domainInfo}>
                  <span className={styles.domainName}>{domain}</span>
                  {type === 'websocket' && (
                    <span className={styles.typeBadge}>WebSocket</span>
                  )}
                </div>
                <span
                  className={clsx(
                    styles.statusIndicator,
                    statusIndicatorClass(status)
                  )}
                >
                  <span
                    className={clsx(styles.statusDot, statusDotClass(status))}
                  />
                  {getStatusLabel(status)}
                </span>
              </li>
            ))}
          </ul>

          {allChecked && (
            <div
              className={clsx(
                styles.summaryBar,
                allSuccess ? styles.summaryBarSuccess : styles.summaryBarError
              )}
            >
              {allSuccess
                ? 'All services are accessible'
                : `${successCount}/${DOMAINS_TO_CHECK.length} services accessible`}
            </div>
          )}

          <button
            type="button"
            className={styles.refreshButton}
            onClick={runChecks}
            disabled={isChecking}
          >
            {isChecking ? 'Checking...' : 'Check Again'}
          </button>
        </div>
      </main>
    </>
  );
};
